import React, { ReactNode } from 'react';
import { StarIcon, TagIcon } from '@heroicons/react/24/outline';
import { twJoin } from 'tailwind-merge';
import { Badge } from '@/ui/components/badge/badge';
import type { ProductBadgeColor, ProductBadgeIcon } from '@/models/product-badge-model';

export type ProductBadgeSize = 'regular' | 'large';

export interface ProductBadgeProps {
  label: string;
  color?: ProductBadgeColor;
  icon?: ProductBadgeIcon;
  /** `large` grows the badge from `lg` up in two steps, for a big product photo like the chooser's. */
  size?: ProductBadgeSize;
  /** Spacing and visibility where the badge sits, e.g. "mt-2 md:hidden". */
  className?: string;
}

interface ProductBadgeLook {
  className: string;
  iconClassName: string;
}

const DEFAULT_LOOK: ProductBadgeLook = {
  className: 'bg-white text-neutral-100 shadow-[inset_0_0_0_1px_#000]',
  iconClassName: 'text-neutral-100',
};

const PRODUCT_BADGE_LOOK_BY_COLOR: Record<ProductBadgeColor, ProductBadgeLook> = {
  black: { className: 'bg-neutral-100 text-white', iconClassName: 'text-primary-100' },
  yellow: { className: 'bg-primary-100 text-neutral-100', iconClassName: 'text-neutral-100' },
  red: { className: 'bg-red text-white', iconClassName: 'text-white' },
};

const CLASS_NAMES_BY_SIZE: Record<ProductBadgeSize, { badge: string; icon: string }> = {
  regular: { badge: '', icon: 'h-4 w-4' },
  // A step up on laptops, the full size only once the photo column is wide enough for it.
  large: {
    badge: 'lg:px-3 lg:py-[9px] lg:text-md 2xl:gap-1.5 2xl:px-4 2xl:py-[15px] 2xl:text-xl 2xl:leading-5',
    icon: 'h-4 w-4 2xl:h-5 2xl:w-5',
  },
};

const ICON_BY_BADGE_ICON: Record<ProductBadgeIcon, (iconClassName: string) => ReactNode> = {
  star: (iconClassName) => <StarIcon className={iconClassName} aria-hidden="true" />,
  tag: (iconClassName) => <TagIcon className={iconClassName} aria-hidden="true" />,
  // The design reads "#1 Bestseller": the "#1" takes the icon's place, the filter stays "Bestseller".
  bestseller: () => <span>#1</span>,
};

export function ProductBadge({
  label, color, icon, size = 'regular', className,
}: Readonly<ProductBadgeProps>) {
  const look = color ? PRODUCT_BADGE_LOOK_BY_COLOR[color] : DEFAULT_LOOK;
  const sizeClassNames = CLASS_NAMES_BY_SIZE[size];
  return (
    <Badge
      icon={icon && ICON_BY_BADGE_ICON[icon](twJoin(sizeClassNames.icon, look.iconClassName))}
      className={twJoin(look.className, sizeClassNames.badge, className)}
    >
      {label}
    </Badge>
  );
}
